import { FC, Fragment, MutableRefObject, ReactNode, useCallback, useEffect, useRef, useState } from "react";

// utils
import { handleError } from "utils/handleError";

// redux
import { useDispatch, useSelector } from "react-redux";
import {
  clearNotifications,
  deleteAllNotifications,
  fetchNotifications,
  fetchNotificationsCount,
  IFetchNotificationsParams,
  readAllNotifications,
} from "redux/thunks/notifications.thunk";
import { AppDispatch } from "redux/store/store";

// router
import { useSearchParams } from "react-router-dom";

// components
import { Badge, Divider, Drawer, Dropdown, Empty, MenuProps, Spin, Tabs } from "antd";
import InfiniteScroll from "components/InfiniteScroll";
import Notification from "./Notification";

// icons
import { ReactComponent as MenuIcon } from "assets/icons/three_dots_icon.svg";
import { ReactComponent as NotificationZero } from "assets/icons/notification_bell.svg";

// style
import css from "./style.module.scss";

// types
import { NotificationCategoryType, NotificationsCountType, NotificationType } from "notifications/types";
import { AppStateType } from "redux/reducers/mainReducer";

enum Tab {
  all = "Все",
  orders = "Заказы",
  bids = "Закупки",
}

const TabFilters: Record<Tab, string> = {
  [Tab.all]: null,
  [Tab.orders]: "order",
  [Tab.bids]: "bid",
};

interface INotificationsPanelProps {
  isOpen: boolean;
  onToggle: () => void;
}

const NotificationsPanel: FC<INotificationsPanelProps> = ({ isOpen, onToggle }) => {
  const dispatch = useDispatch<AppDispatch>();

  const {
    isFetching,
    data,
    count: { bid, order, total },
    next,
  } = useSelector((state: AppStateType) => state.notificationsSlice.notifications);

  const [params, setParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState<Tab>(Tab.all);
  const [searchParams, setSearchParams] = useState<IFetchNotificationsParams>({
    page: 1,
    page_size: 15,
    is_read: null,
    is_purchaser: null,
    category: null,
  });

  const tabBadgeCountMap: Record<Tab, number> = {
    [Tab.all]: total,
    [Tab.orders]: order,
    [Tab.bids]: bid,
  };

  const epmtyTabLabelMap: Record<Tab, string> = {
    [Tab.all]: "",
    [Tab.orders]: "по заказам",
    [Tab.bids]: "по закупкам",
  };

  useEffect(() => {
    applyTabFromUrl();
  }, [params.get("notifications")]);

  useEffect(() => {
    if (isOpen) {
      dispatch(clearNotifications());

      if (!params.get("notifications")) {
        params.set("notifications", activeTab);
        setParams(params, { replace: true });
      }

      fetchByTab();
    }
  }, [activeTab, isOpen]);

  const resetNotifications = (): void => {
    dispatch(clearNotifications());

    setActiveTab(Tab.all);
    updateSearchParams({ page: 1, category: null });

    params.delete("notifications");
    setParams(params, { replace: true });
  };

  const onCloseHandler = (): void => {
    resetNotifications();

    onToggle();
  };

  const updateSearchParams = (updates: Partial<IFetchNotificationsParams>): void => {
    setSearchParams((prev) => ({ ...prev, ...updates }));
  };

  const applyTabFromUrl = (): void => {
    const urlTab: Tab = params.get("notifications") as Tab;

    if (Object.values(Tab).includes(urlTab)) {
      setActiveTab(urlTab);

      !isOpen && onToggle();
    }
  };

  const fetchByTab = (): void => {
    const newParams: IFetchNotificationsParams = {
      ...searchParams,
      page: 1,
      category: TabFilters[activeTab] as NotificationCategoryType,
    };

    updateSearchParams(newParams);
    loadNotifications(newParams);
  };

  const loadNotifications = useCallback((newParams?: IFetchNotificationsParams): void => {
    if (isFetching && !newParams) return;

    const nextParams: IFetchNotificationsParams = {
      ...(newParams ?? searchParams),
      page: (newParams?.page || next || 1)
    };

    dispatch(fetchNotifications(nextParams))
      .catch((error) => handleError("Не удалось загрузить уведомления:", error));
  }, [searchParams, isFetching]);

  const readAllNotificationsHandler = (): void => {
    dispatch(readAllNotifications({ category: TabFilters[activeTab] as NotificationCategoryType }))
      .then((): Promise<NotificationsCountType> => dispatch(fetchNotificationsCount()))
      .catch((error) => handleError("Не удалось прочитать уведомления:", error));
  };

  const deleteAllNotificationsHandler = (): void => {
    dispatch(deleteAllNotifications({ category: TabFilters[activeTab] as NotificationCategoryType }))
      .then((): Promise<NotificationsCountType> => dispatch(fetchNotificationsCount()))
      .catch((error) => handleError("Не удалось удалить уведомления:", error));
  };

  const renderWrapperActions = (): ReactNode => {
    const items: MenuProps["items"] = [
      {
        label: (
          <a>
            Отметить все как прочитанные <span className="ghost text-xs">({activeTab})</span>
          </a>
        ),
        key: "read-all",
        onClick: readAllNotificationsHandler,
        disabled: !tabBadgeCountMap[activeTab]
      },
      {
        label: (
          <a style={!data?.length ? {} : { color: "#FF4D4F" }}>
            Удалить все <span className="ghost text-xs">({activeTab})</span>
          </a>
        ),
        key: "delete-all",
        onClick: deleteAllNotificationsHandler,
        disabled: !data?.length
      },
    ];

    return (
      <Dropdown menu={{ items }} trigger={["click"]}>
        <MenuIcon className="m-auto cursor-pointer secondary-color" />
      </Dropdown>
    );
  };

  const renderTabItems = (
    activeTab: Tab,
    tabBadgeCountMap: Record<Tab, number>
  ) => {
    return Object.values(Tab).map((tab) => {
      const isSelected: boolean = tab === activeTab;
      const textColor: string = isSelected ? "#3438E0" : "#101928";
      const badgeColor: string = isSelected ? "#E4EAFF" : "#F0F1F3";
      const badgeTextColor: string = isSelected ? textColor : "#67768C";
      const sowBadge: boolean = !!tabBadgeCountMap[tab];

      return {
        key: tab,
        label: (
          <div className="flex gap-1 items-center">
            <span style={{ color: textColor }}>{tab}</span>

            {sowBadge && (
              <Badge
                count={tabBadgeCountMap[tab]}
                size="small"
                showZero={false}
                color={badgeColor}
                style={{ color: badgeTextColor }}
              />
            )}
          </div>
        ),
      };
    });
  };

  const renderNotification = (item: NotificationType, index: number) => (
    <Fragment key={`${index}_${item.id}`}>
      <Notification notification={item} />
      <Divider className="m-0" style={{ backgroundColor: "#E1E4E8" }} />
    </Fragment>
  );

  return (
    <Drawer
      key="notifications"
      title="Уведомления"
      placement="right"
      width={378}
      onClose={onCloseHandler}
      open={isOpen}
      extra={(!!tabBadgeCountMap[activeTab] || !!data?.length || isFetching) && renderWrapperActions()}
      classNames={{ body: css.notificationsDrawerBody }}
      styles={{ body: { padding: "0 24px 24px 24px" } }}
    >
      {!!total && (
        <div className={css.stickyTabs}>
          <Tabs
            activeKey={activeTab}
            onChange={(key): void => {
              const selectedTab: Tab = key as Tab;
              setActiveTab(selectedTab);
              params.set("notifications", selectedTab);
              setParams(params);
            }}
            items={renderTabItems(activeTab as Tab || null, tabBadgeCountMap)}
            key="notificationsTab"
          />
        </div>
      )}

      <InfiniteScroll
        className="flex flex-col gap-5"
        fetchNextPage={loadNotifications}
        hasNextPage={!!next}
        isLoading={isFetching}
        isEmpty={!data?.length && !isFetching}
        loadingMessage={<Spin spinning className="flex justify-center pt-5" />}
        endingMessage={<p className="text-xs ghost">Все уведомления загружены</p>}
        emptyMessage={
          <Empty
            image={<NotificationZero />}
            imageStyle={{ height: "34px", textAlign: "center", marginBottom: "12px" }}
            description={
              <div className="flex flex-col items-center">
                <div className={css.textHeader}><strong>Уведомлений пока нет</strong></div>
                <div className={`${css.textBody} ghost`}>
                  Здесь будут отображаться все ваши уведомления {epmtyTabLabelMap?.[activeTab]}
                </div>
              </div>
            }
          />
        }
      >
        {data?.map((entity, index) => renderNotification(entity, index))}
      </InfiniteScroll>
    </Drawer>
  );
};

export default NotificationsPanel;
